type Value = Record<string, unknown>;
export interface TurnNavigationPart { messageID: string; blockID: string; markdown: string; interactive: boolean }
export interface TurnNavigationItem { id: string; user: TurnNavigationPart[]; assistant: TurnNavigationPart[] }

/** The already-projected transcript owns ordering and identity. This function
 * only selects preview blocks; it never reconstructs a native turn or source. */
export function projectTurnNavigation(payload: { messages?: Value[]; blockCatalog?: Value[] }): TurnNavigationItem[] {
  const blocks = new Map<string, Value>();
  for (const block of payload.blockCatalog || []) if (typeof block.id === 'string' && !blocks.has(block.id)) blocks.set(block.id, block);
  const parts = (message: Value, assistant: boolean): TurnNavigationPart[] => {
    if (typeof message.id !== 'string') return [];
    const ids = Array.isArray(message.blocks) ? message.blocks : [];
    return ids.flatMap(id => {
      const block = typeof id === 'string' ? blocks.get(id) : undefined;
      if (!block || block.type !== 'main_text' || typeof block.text !== 'string' || !block.text.trim()
        || (assistant && (block.readexProcessingFoldGroupID != null || block.readexProcessingFoldGroupId != null || block.phase === 'commentary'))) return [];
      // Match the copied host's DIL preflight outside Markdown code, also
      // excluding inline code examples. A quoted <button> is ordinary prose.
      const withoutCode = block.text
        .replace(/(^|\n)[ \t]*(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:\n[ \t]*\2[^\n]*(?=\n|$)|$)/g, '')
        .replace(/(`+)[\s\S]*?\1/g, '');
      return [{ messageID: message.id as string, blockID: id as string, markdown: block.text,
        interactive: Boolean(block.readexIntelligentUIData) || /\{@(?:body|module)\b|<[A-Za-z][\w-]*(?:\s|\/?>)/.test(withoutCode) }];
    });
  };
  const items: TurnNavigationItem[] = [], byID = new Map<string, TurnNavigationItem>();
  for (const message of payload.messages || []) {
    if ((message.role !== 'user' && message.role !== 'steered') || typeof message.id !== 'string' || !message.id.trim() || byID.has(message.id)) continue;
    const item = { id: message.id, user: parts(message, false), assistant: [] };
    items.push(item); byID.set(item.id, item);
  }
  for (const message of payload.messages || []) {
    if (message.role !== 'assistant' || typeof message.replyToMessageID !== 'string') continue;
    byID.get(message.replyToMessageID)?.assistant.push(...parts(message, true));
  }
  return items;
}

/** Serialized into the opaque iframe. Keep every runtime dependency inside
 * this function or pass it explicitly from the frame bootstrap. */
export function installTurnNavigation(project: typeof projectTurnNavigation) {
  const w = window as any;
  if (w.__readosTurnNavigation) return;
  let items: TurnNavigationItem[] = [], scope: string | undefined, selected: string | null = null;
  let root: HTMLElement | null = null, preview: HTMLButtonElement | null = null;
  let edge = 'trailing', frame = 0, dismissTimer = 0, pointerFocus = false;
  let selectionSource: 'pointer' | 'keyboard' = 'pointer';
  let previewRevision = 0, renderedPreviewRevision = -1;
  let rootHovered = false, previewHovered = false;
  const rows = new Map<string, HTMLButtonElement>();
  const cancelDismiss = () => { window.clearTimeout(dismissTimer); dismissTimer = 0; };
  const containsControl = (node: EventTarget | null) => node instanceof Node && Boolean(root?.contains(node) || preview?.contains(node));
  function dismiss() {
    cancelDismiss(); selected = null;
    if (preview) { preview.hidden = true; preview.removeAttribute('data-message-id'); }
    updateMarkers();
  }
  function delayedDismiss(event?: MouseEvent | FocusEvent) {
    if (containsControl(event?.relatedTarget || null)) return;
    cancelDismiss();
    dismissTimer = window.setTimeout(() => {
      const keyboardActive = selectionSource === 'keyboard' && containsControl(document.activeElement);
      if (!keyboardActive && !rootHovered && !previewHovered) dismiss();
    }, 140);
  }
  function article(id: string) {
    return Array.from(document.querySelectorAll<HTMLElement>('#messages article.message[data-message-id]')).find(node => node.dataset.messageId === id);
  }
  function mount() {
    if (root) return;
    root = document.createElement('nav'); root.id = 'reados-turn-navigation'; root.setAttribute('aria-label', '消息导航'); root.hidden = true;
    preview = document.createElement('button'); preview.id = 'reados-turn-preview'; preview.type = 'button'; preview.hidden = true;
    root.addEventListener('pointerenter', () => { rootHovered = true; cancelDismiss(); });
    root.addEventListener('pointerleave', event => { rootHovered = false; delayedDismiss(event); });
    root.addEventListener('focusout', delayedDismiss);
    preview.addEventListener('pointerenter', () => { previewHovered = true; cancelDismiss(); });
    preview.addEventListener('pointerleave', event => { previewHovered = false; delayedDismiss(event); });
    preview.addEventListener('focusin', () => { cancelDismiss(); if (!pointerFocus) selectionSource = 'keyboard'; }); preview.addEventListener('focusout', delayedDismiss);
    preview.addEventListener('click', () => { if (selected) jump(selected); });
    preview.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); const row = selected && rows.get(selected); row && row.focus({ preventScroll: true }); dismiss(); }
    });
    document.body.append(root, preview);
    document.addEventListener('pointerout', event => {
      if (event.relatedTarget !== null) return;
      // CSS :hover can stay latched when the pointer leaves an iframe. The
      // actual boundary event clears pointer intent without stealing keyboard focus.
      rootHovered = false; previewHovered = false;
      if (selectionSource === 'pointer') dismiss();
    }, true);
    document.addEventListener('pointerdown', event => {
      if (!containsControl(event.target)) { dismiss(); return; }
      pointerFocus = true; selectionSource = 'pointer';
      requestAnimationFrame(() => { pointerFocus = false; });
    }, true);
    window.addEventListener('blur', dismiss);
    document.addEventListener('visibilitychange', () => { if (document.hidden) dismiss(); });
    const messages = document.getElementById('messages');
    if (messages) {
      new MutationObserver(() => { previewRevision++; schedule(); }).observe(messages, { childList: true, subtree: true, characterData: true });
      new ResizeObserver(schedule).observe(messages);
    }
    const outline = document.getElementById('readex-message-outline-layer');
    if (outline) new MutationObserver(schedule).observe(outline, { attributes: true, attributeFilter: ['data-edge'] });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
  }
  function jump(id: string) {
    if (!items.some(item => item.id === id) || !article(id)) return;
    // The existing coordinator owns detaching from the live edge, cancellation
    // of pending automatic scrolling, and the actual document offset write.
    w.__chatTranscriptCommandBridge?.execute('scroll_to_message', { messageID: id, alignment: 'top' });
    dismiss(); schedule();
  }
  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; layout(); updateMarkers(); if (selected) showPreview(selected); });
  }
  function layout() {
    if (!root || !preview || !items.length) return;
    const outlineEdge = document.getElementById('readex-message-outline-layer')?.dataset.edge;
    edge = outlineEdge === 'trailing' ? 'leading' : 'trailing';
    root.dataset.edge = edge; preview.dataset.edge = edge;
    const height = Math.min(Math.max(44, items.length * 16), window.innerHeight * .7, 640);
    const pitch = height / items.length;
    root.style.height = `${height}px`; root.style.top = `${(window.innerHeight - height) / 2}px`;
    root.style.setProperty('--reados-turn-pitch', `${pitch}px`);
    root.style.setProperty('--reados-turn-marker-height', `${Math.min(2, Math.max(.5, pitch * .45))}px`);
  }
  function updateMarkers() {
    const selectedIndex = items.findIndex(item => item.id === selected);
    const articles = new Map(Array.from(document.querySelectorAll<HTMLElement>('#messages article.message[data-message-id]')).map(node => [node.dataset.messageId, node]));
    items.forEach((item, index) => {
      const row = rows.get(item.id); if (!row) return;
      const distance = selectedIndex < 0 ? 4 : Math.abs(index - selectedIndex);
      row.style.setProperty('--reados-turn-marker-width', `${[26, 20, 14, 10][distance] || 6}px`);
      const rect = articles.get(item.id)?.getBoundingClientRect();
      const visible = Boolean(rect && rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0);
      row.style.setProperty('--reados-turn-marker-opacity', selected === item.id ? '1' : visible ? '.6' : '.4');
      row.setAttribute('aria-expanded', String(selected === item.id));
      if (selected === item.id) row.setAttribute('aria-describedby', 'reados-turn-preview');
      else row.removeAttribute('aria-describedby');
      row.tabIndex = (selectedIndex < 0 ? index === 0 : selected === item.id) ? 0 : -1;
    });
  }
  // Copy only existing visible text and harmless inline formatting. Never copy
  // IDs, handlers, links, controls, raw DIL fallback, hidden measurement probes,
  // or execute a second Markdown/DIL program just to build the preview.
  function previewParts(parts: TurnNavigationPart[], empty: string, user = false) {
    const fragment = document.createDocumentFragment(); let count = 0;
    const rejected = 'script,style,iframe,canvas,svg,math,button,input,textarea,select,[hidden],[aria-hidden="true"],.height-estimation-probes,.katex-mathml,.reados-dil-raw-fallback,.reados-dil-source-diagnostic,.reados-dil-source-text,[data-reados-app-block-diagnostic]';
    const inline = new Set(['B', 'STRONG', 'EM', 'I', 'CODE', 'DEL', 'S', 'SUB', 'SUP']);
    function copy(node: Node, target: Node) {
      if (count >= 12000) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || '').slice(0, 12000 - count); count += text.length; target.appendChild(document.createTextNode(text)); return;
      }
      if (!(node instanceof HTMLElement) || node.matches(rejected)) return;
      if (node.isConnected) {
        const style = getComputedStyle(node); if (style.display === 'none' || style.visibility === 'hidden') return;
      }
      if (node.tagName === 'BR') { target.appendChild(document.createTextNode(' ')); return; }
      const output = inline.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : document.createDocumentFragment();
      node.childNodes.forEach(child => copy(child, output));
      target.appendChild(output);
      if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'TR'].includes(node.tagName)) target.appendChild(document.createTextNode(' '));
    }
    for (const part of parts) {
      const source = Array.from(article(part.messageID)?.querySelectorAll<HTMLElement>('[data-block-type="main_text"][data-block-key]') || [])
        .find(node => node.dataset.blockKey === part.blockID);
      const interactive = source?.dataset.readosDilStatus === 'plain' ? false : part.interactive;
      const previousText = fragment.textContent?.trim() || '';
      const renderedDIL = source && (source.matches('[data-reados-dil-host],.reados-dil-host') || source.querySelector('[data-reados-dil-host],.reados-dil-host,[data-reados-app-block]'));
      if (!user && source && (!interactive || renderedDIL)) copy(source, fragment);
      if ((user || (fragment.textContent?.trim() || '') === previousText) && !interactive && typeof w.AIReadingUnifiedMarkdown?.renderToHtml === 'function') {
        // User bubbles intentionally display literal Markdown. The iPad rail
        // parses that Markdown; use the same bundled parser without renderer
        // hydration, code execution, DIL compilation, or active HTML insertion.
        try {
          const template = document.createElement('template');
          template.innerHTML = w.AIReadingUnifiedMarkdown.renderToHtml(part.markdown, { allowRawHtml: false, renderMath: false });
          template.content.childNodes.forEach(node => copy(node, fragment));
        } catch { /* An unavailable preview never interrupts the transcript. */ }
      }
      if (count) fragment.appendChild(document.createTextNode(' '));
    }
    if (!fragment.textContent?.trim()) fragment.appendChild(document.createTextNode(parts.some(part => part.interactive) ? '交互内容' : parts.length ? '内容预览暂不可用' : empty));
    return fragment;
  }
  function showPreview(id: string) {
    const item = items.find(value => value.id === id), row = rows.get(id);
    if (!item || !row || !root || !preview) return;
    if (preview.dataset.messageId !== id || renderedPreviewRevision !== previewRevision) {
      const user = document.createElement('span'); user.className = 'reados-turn-preview-user'; user.append(previewParts(item.user, '暂无内容', true));
      const children = [user];
      if (item.assistant.length) {
        const assistant = document.createElement('span'); assistant.className = 'reados-turn-preview-assistant'; assistant.append(previewParts(item.assistant, '内容预览暂不可用')); children.push(assistant);
      }
      preview.replaceChildren(...children); preview.dataset.messageId = id; renderedPreviewRevision = previewRevision;
      preview.setAttribute('aria-label', `跳转到消息：${children.map(child => child.textContent?.trim()).filter(Boolean).join('；').slice(0, 400)}`);
    }
    preview.hidden = false;
    const width = Math.min(336, Math.max(80, window.innerWidth - 60)); preview.style.width = `${width}px`;
    const rowRect = row.getBoundingClientRect(), popupHeight = preview.getBoundingClientRect().height;
    preview.style.left = `${Math.max(8, Math.min(window.innerWidth - width - 8, edge === 'trailing' ? rowRect.left - width - 4 : rowRect.right + 4))}px`;
    preview.style.top = `${Math.max(8, Math.min(window.innerHeight - popupHeight - 8, rowRect.top + rowRect.height / 2 - popupHeight / 2))}px`;
  }
  function select(id: string, source: 'pointer' | 'keyboard') { cancelDismiss(); selectionSource = source; selected = id; updateMarkers(); showPreview(id); }
  w.__readosTurnNavigation = {
    dismissPointer() { rootHovered = false; previewHovered = false; if (selected && selectionSource === 'pointer') dismiss(); },
    update(payload: { messages?: Value[]; blockCatalog?: Value[] }, nextScope: string) {
    mount(); if (!root || !preview) return;
    if (scope !== nextScope) { dismiss(); scope = nextScope; }
    // Reuse the copied ReadOS visual support's palette and stable seed hash.
    const accent = w.__chatTranscriptVisualSupport?.readexAccentColor(String(nextScope || '')) || '#2F8CFF';
    root.style.setProperty('--reados-turn-accent', accent);
    preview.style.setProperty('--reados-turn-accent', accent);
    items = project(payload);
    previewRevision++;
    const ids = new Set(items.map(item => item.id));
    for (const [id, row] of rows) if (!ids.has(id)) { row.remove(); rows.delete(id); }
    items.forEach((item, index) => {
      let row = rows.get(item.id);
      if (!row) {
        row = document.createElement('button'); row.type = 'button'; row.className = 'reados-turn-navigation-row';
        row.dataset.messageId = item.id; row.dataset.turnNavigationId = item.id;
        row.setAttribute('aria-controls', 'reados-turn-preview');
        const marker = document.createElement('span'); marker.className = 'reados-turn-navigation-marker'; marker.setAttribute('aria-hidden', 'true'); row.append(marker);
        row.addEventListener('pointerenter', () => select(item.id, 'pointer'));
        row.addEventListener('focus', () => select(item.id, pointerFocus ? 'pointer' : 'keyboard')); row.addEventListener('click', () => jump(item.id));
        row.addEventListener('keydown', event => {
          const current = items.findIndex(value => value.id === item.id);
          const next = event.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : event.key === 'ArrowUp' ? Math.max(0, current - 1) : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : -1;
          if (next >= 0) { event.preventDefault(); rows.get(items[next].id)?.focus({ preventScroll: true }); }
          else if (event.key === 'Escape') { event.preventDefault(); dismiss(); }
        });
        rows.set(item.id, row);
      }
      row.setAttribute('aria-label', `跳转到第 ${index + 1} 条消息`);
      // Avoid detaching/reinserting the focused row on every streaming update.
      if (root!.children[index] !== row) root!.insertBefore(row, root!.children[index] || null);
    });
    if (selected && !ids.has(selected)) dismiss();
    root.hidden = !items.length; if (!items.length) dismiss();
    layout(); updateMarkers(); if (selected) showPreview(selected); schedule();
  } };
}

export const TURN_NAVIGATION_STYLE = `
#reados-turn-navigation{position:fixed;z-index:30;width:44px;display:flex;flex-direction:column;opacity:1;animation:reados-turn-appear 150ms cubic-bezier(.23,1,.32,1);pointer-events:auto;isolation:isolate}
#reados-turn-navigation[hidden],#reados-turn-preview[hidden]{display:none!important}
#reados-turn-navigation[data-edge="trailing"]{right:0}#reados-turn-navigation[data-edge="leading"]{left:0}
.reados-turn-navigation-row{appearance:none;box-sizing:border-box;flex:0 0 var(--reados-turn-pitch);height:var(--reados-turn-pitch);min-height:0;width:44px;border:0;padding:0 12px;background:transparent;display:flex;align-items:center;justify-content:flex-end;cursor:pointer;color:var(--reados-turn-accent);outline-offset:-2px;border-radius:3px}
#reados-turn-navigation[data-edge="leading"] .reados-turn-navigation-row{justify-content:flex-start}
.reados-turn-navigation-marker{flex:none;display:block;width:var(--reados-turn-marker-width,6px);height:var(--reados-turn-marker-height,2px);border-radius:2px;background:currentColor;opacity:var(--reados-turn-marker-opacity,.4);transition:width 160ms cubic-bezier(.23,1,.32,1),opacity 160ms cubic-bezier(.23,1,.32,1);pointer-events:none}
.reados-turn-navigation-row:focus-visible{outline:2px solid var(--reados-turn-accent)}
#reados-turn-preview{appearance:none;box-sizing:border-box;position:fixed;z-index:50;margin:0;border:1px solid color-mix(in srgb,var(--reados-turn-accent) 12%,transparent);border-radius:14px;padding:14px 24px;background:var(--background-color,#fff);box-shadow:0 8px 30px #0002;color:var(--reados-turn-accent);text-align:left;cursor:pointer;font:400 16px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;white-space:normal;overflow-wrap:anywhere;max-height:calc(100vh - 16px);overflow:hidden}
#reados-turn-preview:focus-visible{outline:2px solid var(--reados-turn-accent);outline-offset:2px}
.reados-turn-preview-user,.reados-turn-preview-assistant{display:-webkit-box;-webkit-box-orient:vertical;overflow:hidden;line-height:26px;max-height:26px;-webkit-line-clamp:1;font-size:16px;letter-spacing:normal}
.reados-turn-preview-user{font-weight:500}.reados-turn-preview-assistant{font-weight:400;-webkit-line-clamp:3;max-height:78px;margin-top:4px;opacity:.6}
#reados-turn-preview code{font-size:.9em;background:transparent;color:inherit;padding:0;white-space:normal}
@keyframes reados-turn-appear{from{opacity:0}to{opacity:1}}
@media(prefers-reduced-motion:reduce){#reados-turn-navigation{animation:none}.reados-turn-navigation-marker{transition:none}}
`;
